import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { FieldActivity } from '../entities/FieldActivity';
import { Subject } from 'rxjs';
import { InFilter } from '../baseServices/filter.interface';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWho, DataChangeEmitWhat } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { EntityHelper, Check } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { GlobalConstants } from '../constant/constant';

@Injectable({
  providedIn: 'root',
})
export class FieldActivityService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<FieldActivity>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(fieldActivity: FieldActivity, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: fieldActivity, who: who, what: what });
  }

  getEmitChangeCallback(fieldActivity: FieldActivity, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForFieldActivity + '_' + fieldActivity.RecordId,
      fn: this.emitChange.bind(this, fieldActivity, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.FieldActivity], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getFieldActivityByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<FieldActivity>(StoreName.FieldActivity, onlineId, FieldActivity, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as FieldActivity;

    if (this.checkIsFieldActivitySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getFieldActivityByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<FieldActivity>(StoreName.FieldActivity, recordId, FieldActivity, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as FieldActivity;

    if (this.checkIsFieldActivitySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getFieldActivitiesByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<FieldActivity>(StoreName.FieldActivity, GlobalConstants.KEY_NAMES.Id, onlineIds, FieldActivity, transaction);
    let records = result as FieldActivity[];

    if (!includeDeleted) {
      records = result.filter(fieldactivity => !this.checkIsFieldActivitySoftDeleted(fieldactivity));
    }

    return records;
  }

  async getFieldActivitiesByProjectId(projectId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<FieldActivity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<FieldActivity>(StoreName.FieldActivity, GlobalConstants.KEY_NAMES.ProjectId, projectId, FieldActivity, transaction);
    let records = result as FieldActivity[];

    if (!includeDeleted) {
      records = result.filter(fieldactivity => !this.checkIsFieldActivitySoftDeleted(fieldactivity));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createFieldActivity(who: DataChangeEmitWho, recordToCreate: FieldActivity, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateFieldActivityCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.FieldActivity, transaction);

    // LC5. Get created record
    const createdRecord = await this.getFieldActivityByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateFieldActivity(
    who: DataChangeEmitWho,
    recordToUpdate: FieldActivity,
    skipLocalCopyValidation: boolean,
    localCopy: FieldActivity | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getFieldActivityByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateFieldActivityUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<FieldActivity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<FieldActivity>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateFieldActivity(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.FieldActivity, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getFieldActivityByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateFieldActivity(fieldActivity: FieldActivity, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateFieldActivityCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateFieldActivityUpdate(fieldActivity: FieldActivity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertFieldActivityFromSync(who: DataChangeEmitWho, recordToUpsert: FieldActivity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getFieldActivityByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateFieldActivity(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createFieldActivity(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  checkIsFieldActivityActive(record: FieldActivity): boolean {
    return record.IsActive;
  }

  private checkIsFieldActivitySoftDeleted(record: FieldActivity): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: FieldActivity, b: FieldActivity): boolean {
    return a.ProjectId === b.ProjectId && a.TaskId === b.TaskId && a.ActivityName === b.ActivityName;
  }

  // #endregion
  // region Lookup


  // #endregion
  // region Transform

  filterFieldActivitiesByProjectId(fieldActivities: FieldActivity[], projectId: number): FieldActivity[] {
    return fieldActivities.filter(fieldActivity => fieldActivity.ProjectId === projectId);
  }

  getDistinctProjectIdsForFieldActivities(fieldActivities: FieldActivity[]): number[] {
    return Array.from(new Set(fieldActivities.map(fieldActivity => fieldActivity.ProjectId)));
  }

  // #endregion
  // region Other

  // #endregion
}
